import React, { useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { GridRowSelectionModel } from '@mui/x-data-grid';
import { useLocation, useNavigate } from 'react-router-dom';
import type { StrapiManagedUserRow, UsersListFilters } from '../../api/strapiUsers';
import { useAuth } from '../../auth/AuthContext';
import { UserPermissionPresetModal } from '../../components/UserPermissionPresetModal';
import { UsersDataGrid } from '../../components/UsersDataGrid';
import {
  useBulkDeactivateManagedUsersMutation,
  useBulkDeleteManagedUsersMutation,
  useBulkReactivateManagedUsersMutation,
  useBulkUpdateManagedUserTypeMutation,
  useCreateManagedUserMutation,
  useMasterEntityMiniListQuery,
  useUsersQuery,
  useUserTypesQuery,
} from '../../query/hooks';
import { appDocumentTitle } from '../../appMeta';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { PageShell } from '../../layout/PageShell';

const ADMIN_ANALIS_LAB_TYPE_CODE = 'admin_analis_lab';

function userTypeCodeForId(
  userTypes: { numericId: number | null; type_code: string }[],
  userTypeId: string,
): string {
  const row = userTypes.find((t) => t.numericId != null && String(t.numericId) === userTypeId);
  return row?.type_code?.trim().toLowerCase() ?? '';
}

function analisTypeDataOptions(rows: Array<Record<string, unknown>> | undefined) {
  return (rows ?? [])
    .map((row) => {
      const id = Number(row.id);
      const title = String(row.title ?? '').trim();
      if (!Number.isInteger(id) || id < 1 || !title) return null;
      return { id: String(id), title };
    })
    .filter((row): row is { id: string; title: string } => row != null)
    .sort((a, b) => a.title.localeCompare(b.title, 'id'));
}

/** Superadmin: user list at `/superadmin/users` (drill-in from row actions). */
export function SuperadminUsersPage() {
  useDocumentTitle(appDocumentTitle('User'));
  const { jwt, user: currentUser, impersonateUser, impersonatePending, isImpersonating } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [impersonateError, setImpersonateError] = useState<string | null>(null);
  const [impersonateTarget, setImpersonateTarget] = useState<{ id: string; label: string } | null>(
    null,
  );
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(() => {
    const state = location.state as { snackbarMessage?: string } | null;
    return state?.snackbarMessage ?? null;
  });

  useEffect(() => {
    const state = location.state as { snackbarMessage?: string } | null;
    if (!state?.snackbarMessage) return;
    navigate(
      { pathname: location.pathname, search: location.search, hash: location.hash },
      { replace: true, state: {} },
    );
  }, [location.pathname, location.search, location.hash, location.state, navigate]);

  const [emailInput, setEmailInput] = useState('');
  const emailContains = useDeferredValue(emailInput.trim());
  const [status, setStatus] = useState<UsersListFilters['status']>('all');
  const filters: UsersListFilters = {
    emailContains,
    status,
  };

  const { data, isPending, isError, error } = useUsersQuery(jwt, filters);

  const { data: userTypes = [], isPending: userTypesPending } = useUserTypesQuery(jwt);
  const { data: analisTypeRows = [], isPending: analisTypesPending } = useMasterEntityMiniListQuery(
    jwt,
    'analis-type-datas',
    Boolean(jwt),
  );
  const analisTypeOptions = analisTypeDataOptions(analisTypeRows);

  const [modalOpen, setModalOpen] = useState(false);
  const [presetModalUserId, setPresetModalUserId] = useState<string | null>(null);
  const [selection, setSelection] = useState<GridRowSelectionModel>([]);
  const [bulkUserTypeModalOpen, setBulkUserTypeModalOpen] = useState(false);
  const [bulkUserTypeId, setBulkUserTypeId] = useState('');
  const [bulkUserTypeError, setBulkUserTypeError] = useState<string | null>(null);
  const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
  const [bulkDeleteError, setBulkDeleteError] = useState<string | null>(null);
  const [bulkDeactivateModalOpen, setBulkDeactivateModalOpen] = useState(false);
  const [bulkDeactivateError, setBulkDeactivateError] = useState<string | null>(null);
  const [bulkReactivateModalOpen, setBulkReactivateModalOpen] = useState(false);
  const [bulkReactivateError, setBulkReactivateError] = useState<string | null>(null);
  const createMutation = useCreateManagedUserMutation(jwt);
  const bulkUserTypeMutation = useBulkUpdateManagedUserTypeMutation(jwt);
  const bulkDeleteMutation = useBulkDeleteManagedUsersMutation(jwt);
  const bulkDeactivateMutation = useBulkDeactivateManagedUsersMutation(jwt);
  const bulkReactivateMutation = useBulkReactivateManagedUsersMutation(jwt);
  const [formUsername, setFormUsername] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formPassword, setFormPassword] = useState('');
  const [formUserTypeId, setFormUserTypeId] = useState('');
  const [formAnalisTypeDataIds, setFormAnalisTypeDataIds] = useState<string[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const formIsAdminAnalisLab = userTypeCodeForId(userTypes, formUserTypeId) === ADMIN_ANALIS_LAB_TYPE_CODE;

  const selectedUserIds = useMemo(() => {
    const selected = new Set(selection.map(String));
    return (data?.rows ?? [])
      .filter((row) => selected.has(row.id) && row.numericId != null)
      .map((row) => String(row.numericId));
  }, [data?.rows, selection]);

  const currentUserId = currentUser?.id != null ? String(currentUser.id) : null;
  const selectedDeletableUserIds = useMemo(
    () => selectedUserIds.filter((id) => id !== currentUserId),
    [selectedUserIds, currentUserId],
  );
  const selectedIncludesSelf = selectedUserIds.length !== selectedDeletableUserIds.length;
  const selectedDeactivatableUserIds = useMemo(() => {
    const selected = new Set(selection.map(String));
    return (data?.rows ?? [])
      .filter(
        (row) =>
          selected.has(row.id) &&
          row.numericId != null &&
          !row.blocked &&
          String(row.numericId) !== currentUserId,
      )
      .map((row) => String(row.numericId));
  }, [data?.rows, selection, currentUserId]);
  const selectedReactivatableUserIds = useMemo(() => {
    const selected = new Set(selection.map(String));
    return (data?.rows ?? [])
      .filter((row) => selected.has(row.id) && row.numericId != null && row.blocked)
      .map((row) => String(row.numericId));
  }, [data?.rows, selection]);
  const selectedIncludesInactive = useMemo(() => {
    const selected = new Set(selection.map(String));
    return (data?.rows ?? []).some((row) => selected.has(row.id) && row.blocked);
  }, [data?.rows, selection]);
  const selectedIncludesActive = useMemo(() => {
    const selected = new Set(selection.map(String));
    return (data?.rows ?? []).some((row) => selected.has(row.id) && !row.blocked);
  }, [data?.rows, selection]);
  const bulkActionsPending =
    bulkUserTypeMutation.isPending ||
    bulkDeactivateMutation.isPending ||
    bulkReactivateMutation.isPending ||
    bulkDeleteMutation.isPending;

  function resetForm() {
    setFormUsername('');
    setFormEmail('');
    setFormPassword('');
    setFormUserTypeId('');
    setFormAnalisTypeDataIds([]);
    setFormError(null);
  }

  function closeModal() {
    setModalOpen(false);
    resetForm();
  }

  function openBulkUserTypeModal() {
    setBulkUserTypeError(null);
    setBulkUserTypeId('');
    setBulkUserTypeModalOpen(true);
  }

  function closeBulkUserTypeModal() {
    if (bulkUserTypeMutation.isPending) return;
    setBulkUserTypeModalOpen(false);
    setBulkUserTypeId('');
    setBulkUserTypeError(null);
  }

  function openBulkDeleteModal() {
    setBulkDeleteError(null);
    setBulkDeleteModalOpen(true);
  }

  function closeBulkDeleteModal() {
    if (bulkDeleteMutation.isPending) return;
    setBulkDeleteModalOpen(false);
    setBulkDeleteError(null);
  }

  function openBulkDeactivateModal() {
    setBulkDeactivateError(null);
    setBulkDeactivateModalOpen(true);
  }

  function closeBulkDeactivateModal() {
    if (bulkDeactivateMutation.isPending) return;
    setBulkDeactivateModalOpen(false);
    setBulkDeactivateError(null);
  }

  function openBulkReactivateModal() {
    setBulkReactivateError(null);
    setBulkReactivateModalOpen(true);
  }

  function closeBulkReactivateModal() {
    if (bulkReactivateMutation.isPending) return;
    setBulkReactivateModalOpen(false);
    setBulkReactivateError(null);
  }

  async function onBulkDeactivateConfirm() {
    setBulkDeactivateError(null);
    if (selectedDeactivatableUserIds.length === 0) {
      setBulkDeactivateError(
        selectedIncludesSelf
          ? 'Cannot deactivate your own account.'
          : 'Select at least one active user.',
      );
      return;
    }
    try {
      const result = await bulkDeactivateMutation.mutateAsync({
        userIds: selectedDeactivatableUserIds,
      });
      setBulkDeactivateModalOpen(false);
      setSelection([]);
      setSnackbarMessage(`Deactivated ${result.deactivatedCount} user(s).`);
    } catch (err) {
      setBulkDeactivateError(err instanceof Error ? err.message : 'Unable to deactivate users.');
    }
  }

  async function onBulkReactivateConfirm() {
    setBulkReactivateError(null);
    if (selectedReactivatableUserIds.length === 0) {
      setBulkReactivateError('Select at least one inactive user.');
      return;
    }
    try {
      const result = await bulkReactivateMutation.mutateAsync({
        userIds: selectedReactivatableUserIds,
      });
      setBulkReactivateModalOpen(false);
      setSelection([]);
      setSnackbarMessage(`Reactivated ${result.reactivatedCount} user(s).`);
    } catch (err) {
      setBulkReactivateError(err instanceof Error ? err.message : 'Unable to reactivate users.');
    }
  }

  async function onBulkDeleteConfirm() {
    setBulkDeleteError(null);
    if (selectedDeletableUserIds.length === 0) {
      setBulkDeleteError(
        selectedIncludesSelf ? 'Cannot delete your own account.' : 'Select at least one user.',
      );
      return;
    }
    try {
      const result = await bulkDeleteMutation.mutateAsync({ userIds: selectedDeletableUserIds });
      setBulkDeleteModalOpen(false);
      setSelection([]);
      setSnackbarMessage(`Deleted ${result.deletedCount} user(s).`);
    } catch (err) {
      setBulkDeleteError(err instanceof Error ? err.message : 'Unable to delete users.');
    }
  }

  async function onBulkUserTypeSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBulkUserTypeError(null);
    if (!bulkUserTypeId.trim()) {
      setBulkUserTypeError('User type is required.');
      return;
    }
    if (selectedUserIds.length === 0) {
      setBulkUserTypeError('Select at least one user.');
      return;
    }
    try {
      const result = await bulkUserTypeMutation.mutateAsync({
        userIds: selectedUserIds,
        userTypeId: bulkUserTypeId.trim(),
      });
      setBulkUserTypeModalOpen(false);
      setBulkUserTypeId('');
      setSelection([]);
      setSnackbarMessage(`User type updated for ${result.updatedCount} user(s).`);
    } catch (err) {
      setBulkUserTypeError(err instanceof Error ? err.message : 'Unable to update user type.');
    }
  }

  async function onCreateSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    if (!formUserTypeId.trim()) {
      setFormError('User type is required.');
      return;
    }
    try {
      await createMutation.mutateAsync({
        username: formUsername.trim(),
        email: formEmail.trim(),
        password: formPassword,
        userTypeId: formUserTypeId.trim(),
        analisTypeDataIds: formIsAdminAnalisLab ? formAnalisTypeDataIds : [],
      });
      closeModal();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Unable to create user.');
    }
  }

  const onImpersonate = useCallback(
    (userId: string) => {
      setImpersonateError(null);
      const label =
        data?.rows.find((r) => r.numericId != null && String(r.numericId) === userId)?.username ??
        userId;
      setImpersonateTarget({ id: userId, label });
    },
    [data?.rows],
  );

  function closeImpersonateModal() {
    if (impersonatePending) return;
    setImpersonateTarget(null);
  }

  async function confirmImpersonate() {
    if (!impersonateTarget || impersonatePending) return;
    const { id } = impersonateTarget;
    setImpersonateError(null);
    try {
      await impersonateUser(id);
    } catch (err) {
      setImpersonateError(err instanceof Error ? err.message : 'Unable to impersonate user.');
    } finally {
      setImpersonateTarget(null);
    }
  }

  const rowActions = useMemo(
    () => ({
      resolveApiId: (row: StrapiManagedUserRow) => row.numericId != null ? String(row.numericId) : null,
      onView: (id: string) =>
        navigate(`/superadmin/users/${encodeURIComponent(id)}/view`),
      onEdit: (id: string) =>
        navigate(`/superadmin/users/${encodeURIComponent(id)}/edit`),
      onAssignPreset: (id: string) => setPresetModalUserId(id),
      onImpersonate,
      impersonateDisabled: isImpersonating || impersonatePending,
    }),
    [navigate, isImpersonating, impersonatePending, onImpersonate],
  );

  return (
    <PageShell title="Users">
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ mb: 1.5 }}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ flex: 1 }}>
            <TextField
              label="Email contains"
              value={emailInput}
              onChange={(ev) => setEmailInput(ev.target.value)}
              size="small"
              fullWidth
              sx={{ maxWidth: { sm: 360 } }}
              inputProps={{ 'data-testid': 'users-filter-email-contains' }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="users-status-filter">Active / inactive</InputLabel>
              <Select
                labelId="users-status-filter"
                label="Active / inactive"
                value={status}
                onChange={(ev) => setStatus(ev.target.value as UsersListFilters['status'])}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ alignSelf: { xs: 'flex-end', sm: 'center' } }}
          >
            <Button
              variant="outlined"
              disabled={selectedUserIds.length === 0 || bulkActionsPending}
              onClick={openBulkUserTypeModal}
              data-testid="users-bulk-user-type"
            >
              Change user type{selectedUserIds.length > 0 ? ` (${selectedUserIds.length})` : ''}
            </Button>
            <Button
              variant="outlined"
              disabled={selectedDeactivatableUserIds.length === 0 || bulkActionsPending}
              onClick={openBulkDeactivateModal}
              data-testid="users-bulk-deactivate"
            >
              Deactivate
              {selectedDeactivatableUserIds.length > 0
                ? ` (${selectedDeactivatableUserIds.length})`
                : ''}
            </Button>
            <Button
              variant="outlined"
              disabled={selectedReactivatableUserIds.length === 0 || bulkActionsPending}
              onClick={openBulkReactivateModal}
              data-testid="users-bulk-reactivate"
            >
              Re-activate
              {selectedReactivatableUserIds.length > 0
                ? ` (${selectedReactivatableUserIds.length})`
                : ''}
            </Button>
            <Button
              variant="outlined"
              color="error"
              disabled={selectedDeletableUserIds.length === 0 || bulkActionsPending}
              onClick={openBulkDeleteModal}
              data-testid="users-bulk-delete"
            >
              Delete{selectedDeletableUserIds.length > 0 ? ` (${selectedDeletableUserIds.length})` : ''}
            </Button>
            <Tooltip title="Register new user">
              <IconButton
                color="primary"
                onClick={() => setModalOpen(true)}
                aria-label="Add user"
                data-testid="users-add-open"
                size="small"
                sx={{ bgcolor: 'action.hover' }}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {impersonateError && (
          <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setImpersonateError(null)}>
            {impersonateError}
          </Alert>
        )}
        {isImpersonating && (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            You are already impersonating another user. Exit impersonation from the account menu
            before switching users.
          </Alert>
        )}

        <UsersDataGrid
          rows={data?.rows ?? []}
          isLoading={isPending}
          isError={isError}
          error={error}
          rowActions={rowActions}
          checkboxSelection
          rowSelectionModel={selection}
          onRowSelectionModelChange={setSelection}
        />
      </Box>

      <Dialog open={modalOpen} onClose={closeModal} fullWidth maxWidth="xs" aria-labelledby="user-create-title">
        <DialogTitle id="user-create-title">Register new user</DialogTitle>
        <Box component="form" onSubmit={onCreateSubmit}>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField
                label="Username"
                value={formUsername}
                onChange={(ev) => setFormUsername(ev.target.value)}
                required
                fullWidth
                autoComplete="username"
                inputProps={{ 'data-testid': 'modal-user-username' }}
              />
              <TextField
                label="Email"
                type="email"
                value={formEmail}
                onChange={(ev) => setFormEmail(ev.target.value)}
                required
                fullWidth
                autoComplete="email"
                inputProps={{ 'data-testid': 'modal-user-email' }}
              />
              <TextField
                label="Password"
                type="password"
                value={formPassword}
                onChange={(ev) => setFormPassword(ev.target.value)}
                required
                fullWidth
                autoComplete="new-password"
                inputProps={{ minLength: 6, 'data-testid': 'modal-user-password' }}
                helperText="Minimum 6 characters."
              />
              <FormControl fullWidth size="small" required disabled={userTypesPending}>
                <InputLabel id="modal-user-type-label">User type</InputLabel>
                <Select
                  labelId="modal-user-type-label"
                  label="User type"
                  value={formUserTypeId}
                  onChange={(ev) => {
                    const next = String(ev.target.value);
                    setFormUserTypeId(next);
                    if (userTypeCodeForId(userTypes, next) !== ADMIN_ANALIS_LAB_TYPE_CODE) {
                      setFormAnalisTypeDataIds([]);
                    }
                  }}
                  displayEmpty
                  renderValue={(v) => {
                    if (!v) return <em>Select user type</em>;
                    const row = userTypes.find((t) => t.numericId != null && String(t.numericId) === v);
                    return row
                      ? `${row.type_code}${row.type_name ? ` — ${row.type_name}` : ''}`
                      : v;
                  }}
                >
                  <MenuItem value="" disabled>
                    <em>Select user type</em>
                  </MenuItem>
                  {userTypes
                    .filter((t) => t.numericId != null)
                    .map((t) => (
                      <MenuItem key={t.numericId} value={String(t.numericId)}>
                        {t.type_code}
                        {t.type_name ? ` — ${t.type_name}` : ''}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              {formIsAdminAnalisLab && (
                <FormControl fullWidth size="small" disabled={analisTypesPending}>
                  <InputLabel id="modal-analis-type-label">Analis type</InputLabel>
                  <Select
                    labelId="modal-analis-type-label"
                    label="Analis type"
                    multiple
                    value={formAnalisTypeDataIds}
                    onChange={(ev) => {
                      const value = ev.target.value;
                      setFormAnalisTypeDataIds(typeof value === 'string' ? value.split(',') : value);
                    }}
                    displayEmpty
                    data-testid="modal-user-analis-type"
                    renderValue={(selected) => {
                      if (selected.length === 0) return <em>Select analis type</em>;
                      return selected
                        .map((id) => analisTypeOptions.find((row) => row.id === id)?.title ?? id)
                        .join(', ');
                    }}
                  >
                    {analisTypeOptions.map((row) => (
                      <MenuItem key={row.id} value={row.id}>
                        {row.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {formError && (
                <Typography variant="body2" color="error">
                  {formError}
                </Typography>
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button type="button" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={
                createMutation.isPending ||
                !formUserTypeId.trim() ||
                userTypesPending
              }
              data-testid="modal-user-submit"
            >
              {createMutation.isPending ? 'Creating…' : 'Create'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog
        open={bulkUserTypeModalOpen}
        onClose={closeBulkUserTypeModal}
        fullWidth
        maxWidth="xs"
        aria-labelledby="bulk-user-type-title"
      >
        <DialogTitle id="bulk-user-type-title">Change user type</DialogTitle>
        <Box component="form" onSubmit={onBulkUserTypeSubmit}>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Update user type for {selectedUserIds.length} selected user
                {selectedUserIds.length === 1 ? '' : 's'}.
              </Typography>
              <FormControl fullWidth size="small" required disabled={userTypesPending}>
                <InputLabel id="bulk-user-type-label">User type</InputLabel>
                <Select
                  labelId="bulk-user-type-label"
                  label="User type"
                  value={bulkUserTypeId}
                  onChange={(ev) => setBulkUserTypeId(String(ev.target.value))}
                  displayEmpty
                  data-testid="bulk-user-type-select"
                  renderValue={(v) => {
                    if (!v) return <em>Select user type</em>;
                    const row = userTypes.find((t) => t.numericId != null && String(t.numericId) === v);
                    return row
                      ? `${row.type_code}${row.type_name ? ` — ${row.type_name}` : ''}`
                      : v;
                  }}
                >
                  <MenuItem value="" disabled>
                    <em>Select user type</em>
                  </MenuItem>
                  {userTypes
                    .filter((t) => t.numericId != null)
                    .map((t) => (
                      <MenuItem key={t.numericId} value={String(t.numericId)}>
                        {t.type_code}
                        {t.type_name ? ` — ${t.type_name}` : ''}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              {bulkUserTypeError && (
                <Typography variant="body2" color="error">
                  {bulkUserTypeError}
                </Typography>
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button type="button" onClick={closeBulkUserTypeModal} disabled={bulkUserTypeMutation.isPending}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={
                bulkUserTypeMutation.isPending ||
                !bulkUserTypeId.trim() ||
                selectedUserIds.length === 0 ||
                userTypesPending
              }
              data-testid="bulk-user-type-submit"
            >
              {bulkUserTypeMutation.isPending ? 'Updating…' : 'Update'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog
        open={bulkDeactivateModalOpen}
        onClose={closeBulkDeactivateModal}
        fullWidth
        maxWidth="xs"
        aria-labelledby="bulk-deactivate-users-title"
      >
        <DialogTitle id="bulk-deactivate-users-title">Deactivate users?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Deactivate {selectedDeactivatableUserIds.length} selected user
            {selectedDeactivatableUserIds.length === 1 ? '' : 's'}? They will become inactive, cannot
            log in, and will move to the end of the list.
          </Typography>
          {selectedIncludesSelf && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Your own account is selected and will be skipped.
            </Typography>
          )}
          {selectedIncludesInactive && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Users that are already inactive will be skipped.
            </Typography>
          )}
          {bulkDeactivateError && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {bulkDeactivateError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            type="button"
            onClick={closeBulkDeactivateModal}
            disabled={bulkDeactivateMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void onBulkDeactivateConfirm()}
            disabled={
              bulkDeactivateMutation.isPending || selectedDeactivatableUserIds.length === 0
            }
            data-testid="users-bulk-deactivate-confirm"
          >
            {bulkDeactivateMutation.isPending ? 'Deactivating…' : 'Deactivate'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={bulkReactivateModalOpen}
        onClose={closeBulkReactivateModal}
        fullWidth
        maxWidth="xs"
        aria-labelledby="bulk-reactivate-users-title"
      >
        <DialogTitle id="bulk-reactivate-users-title">Re-activate users?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Re-activate {selectedReactivatableUserIds.length} selected user
            {selectedReactivatableUserIds.length === 1 ? '' : 's'}? They will become active again,
            can log in, and can be impersonated.
          </Typography>
          {selectedIncludesActive && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Users that are already active will be skipped.
            </Typography>
          )}
          {bulkReactivateError && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {bulkReactivateError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            type="button"
            onClick={closeBulkReactivateModal}
            disabled={bulkReactivateMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void onBulkReactivateConfirm()}
            disabled={
              bulkReactivateMutation.isPending || selectedReactivatableUserIds.length === 0
            }
            data-testid="users-bulk-reactivate-confirm"
          >
            {bulkReactivateMutation.isPending ? 'Re-activating…' : 'Re-activate'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={bulkDeleteModalOpen}
        onClose={closeBulkDeleteModal}
        fullWidth
        maxWidth="xs"
        aria-labelledby="bulk-delete-users-title"
      >
        <DialogTitle id="bulk-delete-users-title">Delete users?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Delete {selectedDeletableUserIds.length} selected user
            {selectedDeletableUserIds.length === 1 ? '' : 's'}? This cannot be undone.
          </Typography>
          {selectedIncludesSelf && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Your own account is selected and will be skipped.
            </Typography>
          )}
          {bulkDeleteError && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {bulkDeleteError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button type="button" onClick={closeBulkDeleteModal} disabled={bulkDeleteMutation.isPending}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => void onBulkDeleteConfirm()}
            disabled={bulkDeleteMutation.isPending || selectedDeletableUserIds.length === 0}
            data-testid="users-bulk-delete-confirm"
          >
            {bulkDeleteMutation.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(impersonateTarget)}
        onClose={closeImpersonateModal}
        fullWidth
        maxWidth="xs"
        aria-labelledby="impersonate-confirm-title"
      >
        <DialogTitle id="impersonate-confirm-title">Impersonate user?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Impersonate user &quot;{impersonateTarget?.label}&quot;? You can exit impersonation from
            the account menu.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button type="button" onClick={closeImpersonateModal} disabled={impersonatePending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void confirmImpersonate()}
            disabled={impersonatePending}
            data-testid="impersonate-confirm"
          >
            Impersonate
          </Button>
        </DialogActions>
      </Dialog>

      <UserPermissionPresetModal
        jwt={jwt}
        userId={presetModalUserId}
        open={Boolean(presetModalUserId)}
        onClose={() => setPresetModalUserId(null)}
      />

      <Snackbar
        open={Boolean(snackbarMessage)}
        autoHideDuration={4000}
        onClose={() => setSnackbarMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ mt: 1, mr: 1 }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSnackbarMessage(null)}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </PageShell>
  );
}
